import type { AccessibleFolderMeta } from '../../organize';
import {
  type FolderApiService,
  PermissionLifecycleOperation,
  PermissionPreflightTargetKind,
} from './folder-api';

export type NavigationPanelFolderVisibility = 'public' | 'private';

type FolderTrashLocalFirstDeps = {
  accessibleDocCache: {
    captureTrashSnapshot(docIds: string[]): unknown;
    restoreTrashSnapshot(snapshot: unknown): void;
    setDocsTrashState(docIds: string[], trashed: boolean): void;
  };
  accessibleFolderCache: {
    captureOptimisticSnapshot(): unknown;
    clearOptimisticTrashedFolder(
      folderId: string,
      outcome?: 'confirmed' | 'rejected'
    ): void;
    fetchAccessibleFolders(): Promise<unknown>;
    flushPendingStateWrites(): Promise<void>;
    getCachedFolderTreeDocIds(folderId: string): string[];
    optimisticRemoveFolder(folderId: string): void;
    optimisticTrashFolder(
      folderId: string,
      yjsFallbackFolders?: AccessibleFolderMeta[]
    ): unknown;
    restoreOptimisticSnapshot(snapshot: unknown): void;
    restoreOptimisticTrashedFolder(folderId: string): void;
  };
  folderApiService: Pick<
    FolderApiService,
    'preflightPermissionLifecycle' | 'restoreFolder' | 'trashFolder'
  >;
  folderId: string;
  folderStore: {
    collectDescendantDocIds(folderId: string): string[];
    getYjsFolderSubtreeSnapshot(
      folderId: string,
      options?: {
        ownerUserId?: string | null;
        visibility?: NavigationPanelFolderVisibility;
      }
    ): AccessibleFolderMeta[];
    restoreFolderSilent(folderId: string): void;
    trashFolderSilent(folderId: string): void;
  };
  canTrashFolder: boolean;
  canUseYjsFolderRoute: boolean;
  canUseServerDestructiveLifecycle: () => boolean;
  workspaceId: string;
  skipPreflight?: boolean;
  notifyConnectionRequired(): void;
  notifyMoveFailed(): void;
  notifyRestoreFailed(): void;
  onTrashConfirmed?: () => void;
  showFolderTrashToast(options: { onUndo: () => Promise<void> }): void;
  visibility?: NavigationPanelFolderVisibility;
  logError?: (message: string, error: unknown) => void;
};

export function moveFolderToTrashLocalFirst({
  accessibleDocCache,
  accessibleFolderCache,
  folderApiService,
  folderId,
  folderStore,
  canTrashFolder,
  canUseYjsFolderRoute,
  canUseServerDestructiveLifecycle,
  workspaceId,
  skipPreflight = false,
  notifyConnectionRequired,
  notifyMoveFailed,
  notifyRestoreFailed,
  onTrashConfirmed,
  showFolderTrashToast,
  visibility = 'public',
  logError = console.error,
}: FolderTrashLocalFirstDeps): Promise<void> {
  if (!canTrashFolder) {
    return Promise.resolve();
  }

  if (!canUseYjsFolderRoute && !canUseServerDestructiveLifecycle()) {
    notifyConnectionRequired();
    return Promise.resolve();
  }

  const folderSnapshot = accessibleFolderCache.captureOptimisticSnapshot();
  const docIds = canUseYjsFolderRoute
    ? folderStore.collectDescendantDocIds(folderId)
    : accessibleFolderCache.getCachedFolderTreeDocIds(folderId);
  const docTrashSnapshot = accessibleDocCache.captureTrashSnapshot(docIds);
  const rollbackLocalTrash = () => {
    accessibleFolderCache.clearOptimisticTrashedFolder(folderId, 'rejected');
    accessibleFolderCache.restoreOptimisticSnapshot(folderSnapshot);
    if (canUseYjsFolderRoute) {
      folderStore.restoreFolderSilent(folderId);
    }
    accessibleDocCache.restoreTrashSnapshot(docTrashSnapshot);
  };

  const run = async () => {
    try {
      if (!canUseYjsFolderRoute) {
        const preflight = skipPreflight
          ? null
          : await folderApiService.preflightPermissionLifecycle({
              workspaceId,
              operation: PermissionLifecycleOperation.FOLDER_TRASH,
              targets: [
                {
                  kind: PermissionPreflightTargetKind.FOLDER,
                  id: folderId,
                },
              ],
            });
        if (preflight && !preflight.allowed) {
          notifyMoveFailed();
          return;
        }
        await folderApiService.trashFolder(workspaceId, folderId);
        accessibleDocCache.setDocsTrashState(
          preflight?.affectedDocIds ?? docIds,
          true
        );
        await accessibleFolderCache.fetchAccessibleFolders().catch(() => {});
        onTrashConfirmed?.();
        showFolderTrashToast({
          onUndo: async () => {
            if (!canUseServerDestructiveLifecycle()) {
              notifyConnectionRequired();
              return;
            }
            try {
              await folderApiService.restoreFolder(workspaceId, folderId);
              await accessibleFolderCache.fetchAccessibleFolders();
              accessibleDocCache.setDocsTrashState(
                preflight?.affectedDocIds ?? docIds,
                false
              );
            } catch (error) {
              logError('Failed to restore folder from trash:', error);
              notifyRestoreFailed();
            }
          },
        });
        return;
      }

      accessibleDocCache.setDocsTrashState(docIds, true);
      accessibleFolderCache.optimisticTrashFolder(
        folderId,
        folderStore.getYjsFolderSubtreeSnapshot(folderId, { visibility })
      );
      await accessibleFolderCache.flushPendingStateWrites();
      folderStore.trashFolderSilent(folderId);
      onTrashConfirmed?.();
      showFolderTrashToast({
        onUndo: async () => {
          try {
            accessibleFolderCache.restoreOptimisticTrashedFolder(folderId);
            await accessibleFolderCache.flushPendingStateWrites();
            folderStore.restoreFolderSilent(folderId);
            accessibleDocCache.setDocsTrashState(docIds, false);
          } catch (error) {
            logError('Failed to restore folder from trash:', error);
            notifyRestoreFailed();
          }
        },
      });
    } catch (error) {
      logError('Failed to move folder to trash locally:', error);
      rollbackLocalTrash();
      await accessibleFolderCache.flushPendingStateWrites();
      notifyMoveFailed();
    }
  };

  return run();
}
